import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { faCircleXmark, faMagnifyingGlassArrowRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Article, ArticleList, articleApi } from '@/modules/articles/export';
import { DataTopic } from '@/modules/calendar/model/data/data-topic';
import { Permissions, RequirePermissions } from '@/core/permissions/export';
import { NewsEvent, NewsEventUtil } from '@/modules/calendar/model/calendar/news-event';
import { Observation } from '@/modules/calendar/model/calendar/observation';
import { InstantAnswers } from '@/modules/calendar/model/calendar/instant-answers';
import { useSubscription, MessageServiceConstants } from '@/core/messaging/export';
import { CountryFlag } from '@/shared/components/export';
import { UpgradePanel } from '@/shared/session/export';
import { DateFormat, Clock } from '@/core/date/export';
import EventBadges from '@/modules/calendar/components/event-badges';
import EventDetailsDataTable from '@/modules/calendar/components/data-calendar/details/event-details-data-table';
import InstantAnswersTable from '@/modules/calendar/components/data-calendar/details/instant-answers-table';
import RelatedArticles from '@/modules/calendar/components/data-calendar/details/related-articles';
import {
  CoverageMap,
  EventCoverage,
} from '@/modules/calendar/components/data-calendar/details/coverage-classes';
import { readerService } from '@/modules/calendar/services';
import {
  analyticsService,
  AnalyticsEvent,
  AnalyticsSubTypeEvent,
  AnalyticsTypes,
} from '@/shared/analytics/export';
import AddToCalendarButton from '@/modules/calendar/components/add-to-calendar-button';
import { useSessionIdentity } from '@/core/auth/export';
import DataReleaseHistory from '@/modules/calendar/components/data/data-release-history';
import { TopicSubscribeButton } from '@/core/notifications/export';
import { Button } from 'react-bootstrap';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { hscWidget } from '@/modules/calendar/widgets/hsc-widget';

export default function CalendarEventDetails({
  newsEvent,
  onClose,
  onArticleClick,
}: {
  newsEvent: NewsEvent;
  onClose: Function;
  onArticleClick: (article: Article) => void;
}) {
  const openWidget = useOpenWidget();
  const identity = useSessionIdentity();
  const { sessionId, subject } = identity;

  const [coverageMap, setCoverageMap] = useState<CoverageMap>(new CoverageMap());
  const [event, setEvent] = useState<NewsEvent>(newsEvent);

  const { leafRegions, leafTopicQcodes } = useMemo(() => {
    const regionTopics = new Map<string, DataTopic>();
    const topicQcodeTopics = new Map<string, DataTopic>();

    event.dataRelease?.series.forEach(value => {
      value.topics.forEach(topic => {
        if (topic.qcode.includes('mnidataregion')) regionTopics.set(topic.qcode, topic);
        if (topic.qcode.includes('mnidatatopics')) topicQcodeTopics.set(topic.qcode, topic);
      });
    });

    const toLeafQcodes = (topicsByQcode: Map<string, DataTopic>): Set<string> => {
      const ancestorQcodes = new Set<string>();
      topicsByQcode.forEach(topic => {
        let broaderQcode = topic.broader;
        while (broaderQcode && !ancestorQcodes.has(broaderQcode)) {
          ancestorQcodes.add(broaderQcode);
          broaderQcode = topicsByQcode.get(broaderQcode)?.broader;
        }
      });
      return new Set(Array.from(topicsByQcode.keys()).filter(qcode => !ancestorQcodes.has(qcode)));
    };

    return {
      leafRegions: toLeafQcodes(regionTopics),
      leafTopicQcodes: toLeafQcodes(topicQcodeTopics),
    };
  }, [event.dataRelease]);

  useEffect(
    () => {
      if (event.coverageSeries) {
        Promise.all(
          event.coverageSeries.map(value => {
            const eventDate = dayjs(event.date);
            const daysAfter = value.coverageType.code === 'PREVIEW' ? -7 : 0;
            const daysBefore = value.coverageType.code === 'PREVIEW' ? 0 : 7;
            return articleApi.getArticlesBySection(undefined, {
              includeGenres: [value.coverageQCode],
              updatedAfter: eventDate.add(daysAfter, 'day').valueOf(),
              updatedBefore: eventDate.add(daysBefore, 'day').valueOf(),
            });
          }),
        ).then(articlePages => {
          articlePages.forEach((articlePage, index) => {
            articlePage.content.forEach(article => {
              setCoverageMap(
                coverageMap.addItem(
                  new EventCoverage(
                    {
                      articleId: article.uri,
                      coverageType: {
                        name: event.coverageSeries![index].coverageType.code,
                        display: event.coverageSeries![index].coverageType.name,
                      },
                    },
                    article,
                  ),
                ),
              );
            });
          });
        });
      }
      Promise.all(
        event.coverage?.map(value =>
          articleApi.getArticle(value.articleId).catch(_ => undefined),
        ) || [],
      ).then(articles => {
        articles.forEach((article, index) => {
          if (!article) return;
          setCoverageMap(coverageMap.addItem(new EventCoverage(event.coverage![index], article)));
        });
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (sessionId) {
      analyticsService.recordAnalyticsEvent(AnalyticsEvent.HSC_EVENT_CLICKED, {
        subject,
        object: {
          type: AnalyticsTypes.EVENT,
          eventSubType: AnalyticsSubTypeEvent.DATA_EVENT,
          id: event.id,
          title: `[${event.country}] ${event.title}`,
          config: {
            date: event.date,
            dataReleaseId: event.dataReleaseId,
            dataSeriesIds: event.dataSeriesEntries.map(ds => ds.dataSeriesId),
            instantAnswers: event.instantAnswers?.map(ia => ({
              instantAnswersSeriesId: ia.instantAnswersSeriesId,
              instantAnswersInstanceId: ia.instantAnswersInstanceId,
              questionIds: ia.questions.map(q => q.questionId),
            })),
          },
        },
        session: {
          id: sessionId,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useSubscription<Observation[]>(MessageServiceConstants.SELECT_TOPIC, message => {
    message.forEach(value => NewsEventUtil.mergeObservations(event, value));
    setEvent({ ...event });
  });

  useSubscription<InstantAnswers[]>(MessageServiceConstants.INSTANT_ANSWERS_TOPIC, message => {
    message.forEach(value => NewsEventUtil.mergeInstantAnswers(event, value));
    setEvent({ ...event });
  });

  const readText = (text: string) => {
    readerService.readScheduledHeadingOr(text);
  };

  const scheduleRead = (id: string) => {
    if (id === '') readerService.scheduleReading();
    else {
      const dataSeriesEntry = event.dataSeriesEntries.find(value => `${value.dataSeriesId}` === id);

      if (!dataSeriesEntry) {
        readerService.scheduleReading();
        return;
      }

      readerService.scheduleReading(
        {
          country: event.country,
          text: dataSeriesEntry.display,
          dataSeriesId: dataSeriesEntry.dataSeriesId,
          event: event.id,
          date: dayjs(event.date).toDate(),
        },
        event,
        sessionId ?? '',
      );
    }
  };

  const isScheduledToRead = (id: number): boolean => {
    return readerService.getDataSeriesReading()?.dataSeriesId === id;
  };

  const tagFilter = {
    ...(leafRegions.size && { region: Array.from(leafRegions) }),
    ...(leafTopicQcodes.size && { topicQcode: Array.from(leafTopicQcodes) }),
  };

  return (
    <div className="container-element h-100 overflow-auto">
      <div className={'container-responsive-text'}>
        <div
          className={'sticky-top z-1 border-bottom pb-1 mb-2 position-relative'}
          style={{ backgroundColor: 'var(--bs-body-bg)' }}
        >
          <div className={'d-flex align-items-start'}>
            <div className={'me-auto'}>
              <h4>
                <CountryFlag country={event.country} height={20} /> <EventBadges event={event} />{' '}
                {event.title}{' '}
              </h4>
              <div className={'text-muted'}>
                <span>
                  {' '}
                  <DateFormat format={'MMM D, h:mm A'} date={event.date} />{' '}
                </span>
              </div>
            </div>

            <div className={'text-end flex-shrink-0'}>
              <div>
                {process.env.REACT_APP_TEST_ENV === 'true' ? (
                  <span className="d-inline-block">
                    <TopicSubscribeButton
                      topicKey="select.data-release.actual"
                      tagFilter={tagFilter}
                      suggestedName={event.title}
                    />
                  </span>
                ) : (
                  <RequirePermissions allow={Permissions.NOTIFICATIONS}>
                    <span className="d-inline-block">
                      <TopicSubscribeButton
                        topicKey="select.data-release.actual"
                        tagFilter={tagFilter}
                        suggestedName={event.title}
                      />
                    </span>
                  </RequirePermissions>
                )}
                <RequirePermissions allow={Permissions.ALPHA_ACCESS_DATA}>
                  <Button
                    size={'sm'}
                    className={'text-primary-emphasis'}
                    onClick={() => openWidget(hscWidget.id, `/event/${event.id}`)}
                  >
                    <FontAwesomeIcon icon={faMagnifyingGlassArrowRight} />
                  </Button>
                </RequirePermissions>
                <span className={'d-inline-block'}>
                  <AddToCalendarButton
                    event={event}
                    type={'data'}
                    className={'link-warning'}
                    identity={identity}
                  />
                </span>
                <span>
                  <button
                    onClick={() => onClose()}
                    style={{ fontFamily: 'Inter' }}
                    className={'btn btn-sm btn-link link-error'}
                  >
                    <FontAwesomeIcon icon={faCircleXmark} />
                  </button>
                </span>
              </div>
            </div>
          </div>
          <div className="position-absolute start-50 translate-middle-x" style={{ bottom: '4px' }}>
            <Clock />
          </div>
        </div>

        <RequirePermissions deny={Permissions.EXCLUDE_NEWS}>
          {coverageMap
            ?.coverageTypes()
            .sort((a, b) => {
              if (a.display < b.display) return -1;
              else if (a.display < b.display) return 1;
              return 0;
            })
            .map(value => {
              return (
                <div className={'mb-3'} key={'coverage-' + value.name}>
                  <h5 className={'calendar-section-header'}>
                    <span className={'text-secondary'}> {value.display}</span> Coverage
                  </h5>

                  <ArticleList
                    id={`coverage-${value.name}`}
                    articles={coverageMap?.articlesByType(value.name)}
                    onArticleClick={onArticleClick}
                  />
                </div>
              );
            })}
        </RequirePermissions>

        {event.dataSeriesEntries && event.dataSeriesEntries.length > 0 && (
          <div className={'mb-3'}>
            <h5 className={'calendar-section-header fw-bold'}>
              <span className={'text-secondary'}>MNI</span> Data
            </h5>
            <EventDetailsDataTable event={event} date={newsEvent.date} />
            {dayjs(event.date).isAfter(dayjs()) && (
              <>
                <h6 className={'calendar-section-header fw-bold'}>Data Reader</h6>
                <p className={'text-muted'}>
                  This will read an incoming data figure out loud as it is received.
                </p>

                <div className="input-group ">
                  <select
                    className={'form-control form-control-sm'}
                    onChange={event1 => scheduleRead(event1.target.value)}
                  >
                    <option value={''}> - No Reading -</option>
                    {event.dataSeriesEntries.map(value => (
                      <option
                        key={value.dataSeriesId}
                        selected={isScheduledToRead(value.dataSeriesId)}
                        value={value.dataSeriesId}
                      >
                        {value.display}
                      </option>
                    ))}
                  </select>
                  <button
                    style={{ fontFamily: 'Inter' }}
                    className={'btn btn-sm btn-outline-success bg-quaternary'}
                    onClick={() => readText('No Reading Scheduled')}
                  >
                    Preview
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {event.instantAnswers && event.instantAnswers.length > 0 && (
          <div className={'mb-3'}>
            <h5 className={'calendar-section-header fw-bold'}>
              <span className={'text-secondary'}>MNI</span> Instant Answers
            </h5>

            {event.instantAnswers.map(ia => {
              const iaDisplay = () => {
                switch (ia.state) {
                  case 'NOT_AUTHORIZED':
                    return <UpgradePanel contentName={ia.display} identity={identity} />;
                  case 'AWAITING_DEFINITION':
                    return (
                      <div className={'alert alert-warning text-center'}>
                        <p>
                          <strong>
                            <em>{ia.display}</em> Questions will be available 24 hours before event
                          </strong>
                        </p>
                        <p>
                          Our reporters are formulating our instant answers broadcast based on
                          current events and conditions.
                        </p>
                        <p>
                          <em>Check back 24 hours before the event!</em>
                        </p>
                      </div>
                    );
                  default:
                    return (
                      <>
                        <h6>{ia.display}</h6>
                        <InstantAnswersTable instantAnswers={ia} />
                      </>
                    );
                }
              };

              return <div key={`ia-${ia.display}`}>{iaDisplay()}</div>;
            })}
          </div>
        )}

        {event.dataReleaseId && (
          <div className={'mb-3'}>
            <h5 className={'calendar-section-header fw-bold'}>
              <span className={'text-secondary'}>MNI</span> Data History
            </h5>
            <DataReleaseHistory dataReleaseId={event.dataReleaseId} date={event.date} />
          </div>
        )}

        <RequirePermissions allow={Permissions.BETA_ACCESS}>
          <div>
            <h5 className={'calendar-section-header'}>
              <span className={'text-secondary'}>MNI</span> Related Articles
            </h5>
            <RelatedArticles event={event} onArticleClick={onArticleClick} />
          </div>
        </RequirePermissions>
      </div>
    </div>
  );
}
